import { Box, Dialog, DialogActions, DialogContent, Typography } from "@mui/material"
import type { FC } from "react"
import React, { useMemo } from "react"

import { Button } from "@/components/common/Button"
import CircularLoadingIndicator from "@/components/common/LoadingIndicators/CircularLoadingIndicator"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { CompanyCertificationType } from "@/graphql/codegen/graphql"
import HistoricCertificationTable from "@/screens/Companies/edit/components/certification/components/tables/HistoricCertificationTable"
import useGetCompanyCertification from "@/screens/Companies/edit/components/certification/hooks/useGetCompanyCertification"
import {
  getCertificationLabel,
  groupCertificationsByLocation,
} from "@/screens/Companies/edit/components/certification/utils"

interface HistoricCertificatesDialogProps {
  certificationType: CompanyCertificationType
  companyId: string
  onClose: () => void
}

const HistoricCertificatesDialog: FC<HistoricCertificatesDialogProps> = ({ companyId, certificationType, onClose }) => {
  const { companyCertifications, fetching } = useGetCompanyCertification({
    companyId,
    certificationType,
    isExpired: true,
  })

  const groupedByLocation = useMemo(() => groupCertificationsByLocation(companyCertifications), [companyCertifications])

  return (
    <Dialog open onClose={onClose} maxWidth='lg' fullWidth>
      <DialogHeader title='Historic Certificates' onClose={onClose} />
      <DialogContent classes={{ root: "px-10" }}>
        <Typography variant='body1' className='font-medium text-gray-900'>
          {getCertificationLabel(certificationType)}
        </Typography>

        {fetching ? (
          <CircularLoadingIndicator />
        ) : (
          <Box className='mt-6'>
            <HistoricCertificationTable data={groupedByLocation} />
          </Box>
        )}
      </DialogContent>
      <DialogActions className='justify-end px-10 pb-10'>
        <Box className='flex gap-2'>
          <Button onClick={onClose} data-testid='add-certification-save-button'>
            Close
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default HistoricCertificatesDialog
